/**
 * Business Reviews Component
 * 
 * Provides functionality for:
 * - Viewing business reviews with pagination
 * - Submitting new reviews
 * - Rating with stars
 * - Marking reviews as helpful
 * - Reporting inappropriate reviews
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar,
  faStarHalfAlt,
  faUser,
  faThumbsUp,
  faFlag,
  faChevronDown,
  faChevronUp,
  faReply,
  faEdit,
  faTrash,
  faCalendarAlt,
  faSort,
  faTimes,
  faSpinner,
  faPen
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/CombinedAuthContext';
import { useToast } from './ui';
import { useSignupGate } from './auth/SignupGate';
import '../styles/Reviews.css';

const functions = getFunctions(undefined, 'us-central1');

/**
 * Star Rating Display
 */
export function StarRating({ rating, size = 'medium', showValue = false }) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className={`star-rating star-rating--${size}`}>
      <div className="stars">
        {[...Array(fullStars)].map((_, i) => (
          <FontAwesomeIcon key={`full-${i}`} icon={faStar} className="star filled" />
        ))}
        {hasHalfStar && <FontAwesomeIcon icon={faStarHalfAlt} className="star half" />}
        {[...Array(emptyStars)].map((_, i) => (
          <FontAwesomeIcon key={`empty-${i}`} icon={faStarOutline} className="star empty" />
        ))}
      </div>
      {showValue && <span className="rating-value">{rating.toFixed(1)}</span>}
    </div>
  );
}

/**
 * Interactive Star Rating Input
 */
export function StarRatingInput({ value, onChange, disabled = false }) {
  const [hoverRating, setHoverRating] = useState(0);
  const displayRating = hoverRating || value;

  return (
    <div className={`star-rating-input ${disabled ? 'disabled' : ''}`}>
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          className="star-btn"
          onMouseEnter={() => !disabled && setHoverRating(star)}
          onMouseLeave={() => !disabled && setHoverRating(0)}
          onClick={() => !disabled && onChange(star)}
          disabled={disabled}
        >
          <FontAwesomeIcon 
            icon={star <= displayRating ? faStar : faStarOutline} 
            className={`star ${star <= displayRating ? 'filled' : 'empty'}`}
          />
        </button>
      ))}
      {displayRating > 0 && (
        <span className="rating-label">
          {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][displayRating]}
        </span>
      )}
    </div>
  );
}

/**
 * Review Stats Summary
 */
export function ReviewStats({ stats, onFilterByRating }) {
  if (!stats || stats.totalReviews === 0) {
    return (
      <div className="review-stats empty">
        <p>No reviews yet</p>
      </div>
    );
  }

  const { totalReviews, averageRating, ratingDistribution, responseRate, recentTrend } = stats;

  return (
    <div className="review-stats">
      <div className="stats-overview">
        <div className="average-rating">
          <span className="rating-number">{averageRating.toFixed(1)}</span>
          <StarRating rating={averageRating} size="large" />
          <span className="total-reviews">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</span>
        </div>
        
        {recentTrend !== 'neutral' && (
          <div className={`trend-indicator ${recentTrend}`}>
            <FontAwesomeIcon icon={recentTrend === 'up' ? faChevronUp : faChevronDown} />
            <span>Trending {recentTrend}</span>
          </div>
        )}
      </div>

      <div className="rating-breakdown">
        {[5, 4, 3, 2, 1].map(rating => {
          const count = ratingDistribution[rating] || 0;
          const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
          
          return (
            <button
              key={rating}
              className="rating-bar-row"
              onClick={() => onFilterByRating?.(rating)}
            >
              <span className="rating-label">{rating} star</span>
              <div className="rating-bar">
                <div 
                  className="rating-bar-fill" 
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="rating-count">{count}</span>
            </button>
          );
        })}
      </div>

      {responseRate > 0 && (
        <div className="response-rate">
          <FontAwesomeIcon icon={faReply} />
          <span>Business responds to {responseRate}% of reviews</span>
        </div>
      )}
    </div>
  );
}

/**
 * Single Review Card
 */
export function ReviewCard({ 
  review, 
  currentUserId,
  isBusinessOwner = false,
  onMarkHelpful,
  onReport,
  onRespond,
  onEditReview,
  onDeleteReview,
  onEditResponse,
  onDeleteResponse
}) {
  const [showFullContent, setShowFullContent] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const isOwn = review.userId === currentUserId;
  const hasLongContent = review.content?.length > 300;
  const displayContent = hasLongContent && !showFullContent 
    ? review.content.substring(0, 300) + '...' 
    : review.content;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const isHelpful = review.helpfulBy?.includes(currentUserId);

  return (
    <div className={`review-card ${isOwn ? 'own-review' : ''}`}>
      <div className="review-header">
        <div className="reviewer-info">
          {review.userAvatar ? (
            <img src={review.userAvatar} alt="" className="reviewer-avatar" />
          ) : (
            <div className="reviewer-avatar default">
              <FontAwesomeIcon icon={faUser} />
            </div>
          )}
          <div className="reviewer-details">
            <span className="reviewer-name">{review.userDisplayName}</span>
            <span className="review-date">{formatDate(review.createdAt)}</span>
          </div>
        </div>
        
        <div className="review-rating">
          <StarRating rating={review.rating} size="small" />
        </div>
      </div>

      {review.title && (
        <h4 className="review-title">{review.title}</h4>
      )}

      <div className="review-content">
        <p>{displayContent}</p>
        {hasLongContent && (
          <button 
            className="read-more-btn"
            onClick={() => setShowFullContent(!showFullContent)}
          >
            {showFullContent ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>

      {review.visitDate && (
        <div className="visit-date">
          <FontAwesomeIcon icon={faCalendarAlt} />
          <span>Visited {formatDate(review.visitDate)}</span>
        </div>
      )}

      {/* Business Response */}
      {review.response && (
        <div className="business-response">
          <div className="response-header">
            <FontAwesomeIcon icon={faReply} />
            <span className="responder-name">{review.response.responderName}</span>
            <span className="response-date">
              {formatDate(review.response.respondedAt)}
              {review.response.editedAt && ' (edited)'}
            </span>
          </div>
          <p className="response-text">{review.response.text}</p>
          
          {isBusinessOwner && (
            <div className="response-actions">
              <button 
                className="action-btn"
                onClick={() => onEditResponse?.(review.id)}
              >
                <FontAwesomeIcon icon={faEdit} /> Edit
              </button>
              <button 
                className="action-btn danger"
                onClick={() => onDeleteResponse?.(review.id)}
              >
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="review-actions">
        {!isOwn && (
          <>
            <button 
              className={`action-btn ${isHelpful ? 'active' : ''}`}
              onClick={() => onMarkHelpful?.(review.id, !isHelpful)}
              title={isHelpful ? 'Remove helpful' : 'Mark as helpful'}
            >
              <FontAwesomeIcon icon={faThumbsUp} />
              <span>{review.helpful || 0}</span>
            </button>
            
            <button 
              className="action-btn"
              onClick={() => setShowReportModal(true)}
              title="Report review"
            >
              <FontAwesomeIcon icon={faFlag} />
            </button>
          </>
        )}

        {isOwn && (
          <>
            <button 
              className="action-btn"
              onClick={() => onEditReview?.(review)}
            >
              <FontAwesomeIcon icon={faEdit} /> Edit
            </button>
            <button 
              className="action-btn danger"
              onClick={() => onDeleteReview?.(review.id)}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete
            </button>
          </>
        )}

        {isBusinessOwner && !review.response && (
          <button 
            className="action-btn primary"
            onClick={() => onRespond?.(review.id)}
          >
            <FontAwesomeIcon icon={faReply} /> Respond
          </button>
        )}
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal 
          reviewId={review.id}
          onClose={() => setShowReportModal(false)}
          onSubmit={(reason, details) => {
            onReport?.(review.id, reason, details);
            setShowReportModal(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Report Review Modal
 */
function ReportModal({ reviewId, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');

  const reasons = [
    { value: 'spam', label: 'Spam or advertisement' },
    { value: 'inappropriate', label: 'Inappropriate content' },
    { value: 'fake', label: 'Fake or misleading review' },
    { value: 'offensive', label: 'Offensive language' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="report-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Report Review</h3>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group">
            <label>Reason for reporting</label>
            <select 
              value={reason} 
              onChange={e => setReason(e.target.value)}
              required
            >
              <option value="">Select a reason</option>
              {reasons.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Additional details (optional)</label>
            <textarea
              value={details}
              onChange={e => setDetails(e.target.value)}
              placeholder="Provide more context..."
              rows={3}
            />
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn secondary" onClick={onClose}>Cancel</button>
          <button 
            className="btn primary"
            onClick={() => onSubmit(reason, details)}
            disabled={!reason}
          >
            Submit Report
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Write Review Form
 */
export function WriteReviewForm({ 
  businessId, 
  businessName,
  existingReview = null,
  onSubmit,
  onCancel 
}) {
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [title, setTitle] = useState(existingReview?.title || '');
  const [content, setContent] = useState(existingReview?.content || '');
  const [visitDate, setVisitDate] = useState(existingReview?.visitDate?.split('T')[0] || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (rating === 0) newErrors.rating = 'Please select a rating';
    if (content.length < 10) newErrors.content = 'Review must be at least 10 characters';
    if (content.length > 2000) newErrors.content = 'Review must be less than 2000 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        rating,
        title,
        content,
        visitDate: visitDate || null
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="write-review-form" onSubmit={handleSubmit}>
      <h3>{existingReview ? 'Edit Review' : `Review ${businessName}`}</h3>

      <div className="form-group">
        <label>Your Rating *</label>
        <StarRatingInput 
          value={rating} 
          onChange={setRating}
          disabled={isSubmitting}
        />
        {errors.rating && <span className="error">{errors.rating}</span>}
      </div>

      <div className="form-group">
        <label>Review Title (optional)</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Summarize your experience"
          maxLength={100}
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label>Your Review *</label>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Share your experience with this business..."
          rows={5}
          maxLength={2000}
          disabled={isSubmitting}
        />
        <div className="char-count">
          {content.length}/2000 characters
        </div>
        {errors.content && <span className="error">{errors.content}</span>}
      </div>

      <div className="form-group">
        <label>Date of Visit (optional)</label>
        <input
          type="date"
          value={visitDate}
          onChange={e => setVisitDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          disabled={isSubmitting}
        />
      </div>

      <div className="form-actions">
        {onCancel && (
          <button 
            type="button" 
            className="btn secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        <button 
          type="submit" 
          className="btn primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Submitting...
            </>
          ) : (
            existingReview ? 'Update Review' : 'Submit Review'
          )}
        </button>
      </div>
    </form>
  );
}

/**
 * Business Response Form
 */
export function ResponseForm({ reviewId, existingResponse = null, onSubmit, onCancel }) {
  const [text, setText] = useState(existingResponse?.text || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (text.length < 5) {
      setError('Response must be at least 5 characters');
      return;
    }
    if (text.length > 1000) {
      setError('Response must be less than 1000 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(text);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="response-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Your Response</label>
        <textarea
          value={text}
          onChange={e => {
            setText(e.target.value);
            setError('');
          }}
          placeholder="Respond to this review..."
          rows={4}
          maxLength={1000}
          disabled={isSubmitting}
        />
        <div className="char-count">{text.length}/1000 characters</div>
        {error && <span className="error">{error}</span>}
      </div>

      <div className="form-actions">
        <button 
          type="button" 
          className="btn secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn primary"
          disabled={isSubmitting || text.length < 5}
        >
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Saving...
            </>
          ) : (
            existingResponse ? 'Update Response' : 'Post Response'
          )}
        </button>
      </div>
    </form>
  );
}

/**
 * Main Reviews Section Component
 */
export function ReviewsSection({ businessId, businessName, isBusinessOwner = false }) {
  const { user: currentUser, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const requireAuth = useSignupGate();
  
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastReviewId, setLastReviewId] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);
  const [editingResponse, setEditingResponse] = useState(null);
  const [userHasReviewed, setUserHasReviewed] = useState(false);

  // Fetch reviews
  const fetchReviews = useCallback(async (append = false) => {
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      const getBusinessReviews = httpsCallable(functions, 'getBusinessReviews');
      const result = await getBusinessReviews({
        businessId,
        sortBy,
        limit: 10,
        lastReviewId: append ? lastReviewId : null
      });

      if (result.data.success) {
        const newReviews = result.data.reviews;
        setReviews(prev => append ? [...prev, ...newReviews] : newReviews);
        setHasMore(result.data.hasMore);
        setLastReviewId(result.data.lastReviewId);
        
        // Check if current user has reviewed
        if (currentUser && !append) {
          const hasReviewed = newReviews.some(r => r.userId === currentUser.uid);
          setUserHasReviewed(hasReviewed);
        }
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      showToast('Failed to load reviews', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [businessId, sortBy, lastReviewId, currentUser, showToast]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const getReviewStats = httpsCallable(functions, 'getReviewStats');
      const result = await getReviewStats({ businessId });
      if (result.data.success) {
        setStats(result.data.stats);
      }
    } catch (error) {
      console.error('Error fetching review stats:', error);
    }
  }, [businessId]);

  // Initial load
  useEffect(() => {
    fetchReviews();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, sortBy]);

  // Submit review
  const handleSubmitReview = async (reviewData) => {
    try {
      const submitReview = httpsCallable(functions, 'submitReview');
      await submitReview({ businessId, ...reviewData });
      showToast('Review submitted successfully!', 'success');
      setShowWriteReview(false);
      setUserHasReviewed(true);
      fetchReviews();
      fetchStats();
    } catch (error) {
      showToast(error.message || 'Failed to submit review', 'error');
      throw error;
    }
  };

  // Update review
  const handleUpdateReview = async (reviewData) => {
    try {
      const updateReview = httpsCallable(functions, 'updateReview');
      await updateReview({ reviewId: editingReview.id, ...reviewData });
      showToast('Review updated successfully!', 'success');
      setEditingReview(null);
      fetchReviews();
      fetchStats();
    } catch (error) {
      showToast(error.message || 'Failed to update review', 'error');
      throw error;
    }
  };

  // Delete review
  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      const deleteReview = httpsCallable(functions, 'deleteReview');
      await deleteReview({ reviewId });
      showToast('Review deleted', 'success');
      setUserHasReviewed(false);
      fetchReviews();
      fetchStats();
    } catch (error) {
      showToast(error.message || 'Failed to delete review', 'error');
    }
  };

  // Mark helpful
  const handleMarkHelpful = async (reviewId, helpful) => {
    if (!isAuthenticated) {
      const ok = await requireAuth({
        audience: "traveler",
        intent: "review-helpful",
        title: "Help yer fellow crew",
        message:
          "Mark reviews helpful so other travelers know what to expect. Free Deckhand sign-up, no card required.",
      });
      if (!ok) return;
      return; // Gate redirects away.
    }
    
    try {
      const markHelpful = httpsCallable(functions, 'markHelpful');
      await markHelpful({ reviewId, helpful });
      // Update local state
      setReviews(prev => prev.map(r => {
        if (r.id !== reviewId) return r;
        const helpfulBy = r.helpfulBy || [];
        return {
          ...r,
          helpful: helpful 
            ? (r.helpful || 0) + 1 
            : Math.max(0, (r.helpful || 0) - 1),
          helpfulBy: helpful
            ? [...helpfulBy, currentUser.uid]
            : helpfulBy.filter(id => id !== currentUser.uid)
        };
      }));
    } catch (error) {
      showToast(error.message || 'Failed to update', 'error');
    }
  };

  // Report review
  const handleReport = async (reviewId, reason, details) => {
    try {
      const reportReview = httpsCallable(functions, 'reportReview');
      await reportReview({ reviewId, reason, details });
      showToast('Review reported. Thank you for your feedback.', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to report review', 'error');
    }
  };

  // Respond to review
  const handleRespond = async (text) => {
    try {
      const respondToReview = httpsCallable(functions, 'respondToReview');
      await respondToReview({ reviewId: respondingTo, responseText: text });
      showToast('Response posted!', 'success');
      setRespondingTo(null);
      fetchReviews();
    } catch (error) {
      showToast(error.message || 'Failed to post response', 'error');
      throw error;
    }
  };

  // Edit response
  const handleEditResponse = async (text) => {
    try {
      const editResponse = httpsCallable(functions, 'editResponse');
      await editResponse({ reviewId: editingResponse, responseText: text });
      showToast('Response updated!', 'success');
      setEditingResponse(null);
      fetchReviews();
    } catch (error) {
      showToast(error.message || 'Failed to update response', 'error');
      throw error;
    }
  };

  // Delete response
  const handleDeleteResponse = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this response?')) return;
    
    try {
      const deleteResponse = httpsCallable(functions, 'deleteResponse');
      await deleteResponse({ reviewId });
      showToast('Response deleted', 'success');
      fetchReviews();
    } catch (error) {
      showToast(error.message || 'Failed to delete response', 'error');
    }
  };

  const canWriteReview = isAuthenticated && !userHasReviewed && !isBusinessOwner;

  if (loading) {
    return (
      <div className="reviews-section loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="reviews-section">
      <div className="reviews-header">
        <h3>Reviews</h3>
        {canWriteReview && !showWriteReview && (
          <button 
            className="btn primary"
            onClick={() => setShowWriteReview(true)}
          >
            <FontAwesomeIcon icon={faPen} /> Write a Review
          </button>
        )}
      </div>

      {/* Stats */}
      <ReviewStats 
        stats={stats} 
        onFilterByRating={(rating) => {
          // Could filter reviews by rating
        }}
      />

      {/* Write Review Form */}
      {showWriteReview && (
        <WriteReviewForm
          businessId={businessId}
          businessName={businessName}
          onSubmit={handleSubmitReview}
          onCancel={() => setShowWriteReview(false)}
        />
      )}

      {/* Edit Review Form */}
      {editingReview && (
        <WriteReviewForm
          businessId={businessId}
          businessName={businessName}
          existingReview={editingReview}
          onSubmit={handleUpdateReview}
          onCancel={() => setEditingReview(null)}
        />
      )}

      {/* Sort Options */}
      {reviews.length > 0 && (
        <div className="reviews-sort">
          <label>
            <FontAwesomeIcon icon={faSort} />
            Sort by:
          </label>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="recent">Most Recent</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
            <option value="helpful">Most Helpful</option>
          </select>
        </div>
      )}

      {/* Reviews List */}
      <div className="reviews-list">
        {reviews.length === 0 ? (
          <div className="no-reviews">
            <p>No reviews yet. Be the first to review!</p>
          </div>
        ) : (
          reviews.map(review => (
            <React.Fragment key={review.id}>
              <ReviewCard
                review={review}
                currentUserId={currentUser?.uid}
                isBusinessOwner={isBusinessOwner}
                onMarkHelpful={handleMarkHelpful}
                onReport={handleReport}
                onRespond={(reviewId) => setRespondingTo(reviewId)}
                onEditReview={setEditingReview}
                onDeleteReview={handleDeleteReview}
                onEditResponse={(reviewId) => setEditingResponse(reviewId)}
                onDeleteResponse={handleDeleteResponse}
              />
              
              {/* Response Form */}
              {respondingTo === review.id && (
                <ResponseForm
                  reviewId={review.id}
                  onSubmit={handleRespond}
                  onCancel={() => setRespondingTo(null)}
                />
              )}
              
              {/* Edit Response Form */}
              {editingResponse === review.id && (
                <ResponseForm
                  reviewId={review.id}
                  existingResponse={review.response}
                  onSubmit={handleEditResponse}
                  onCancel={() => setEditingResponse(null)}
                />
              )}
            </React.Fragment>
          ))
        )}
      </div>

      {/* Load More */}
      {hasMore && (
        <button 
          className="load-more-btn"
          onClick={() => fetchReviews(true)}
          disabled={loadingMore}
        >
          {loadingMore ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Loading...
            </>
          ) : (
            'Load More Reviews'
          )}
        </button>
      )}

      {/* Login prompt */}
      {!isAuthenticated && (
        <div className="login-prompt">
          <p>Log in to write a review or mark reviews as helpful</p>
        </div>
      )}
    </div>
  );
}

export default ReviewsSection;
